import React, { useState, useCallback, useEffect, useMemo, useId, FC } from 'react';
import Cropper from 'react-easy-crop';
import { PlusIcon, XCircleIcon, RotateCcwIcon, RotateCwIcon } from 'lucide-react';

import { NbButton } from '@/components/Neobrut';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

import getCroppedImg from '@/utils/getCroppedImg';

interface ImageThumbnailProps {
  src: string;
  alt?: string;
  onRemove?: () => void;
}

const ImageThumbnail: FC<ImageThumbnailProps> = ({ src, alt = 'Preview', onRemove }) => (
  <div className="relative">
    <img
      src={src}
      alt={alt}
      className="h-20 w-20 object-cover"
      style={{
        border: '2px solid var(--nb-ink)',
        boxShadow: '2px 2px 0 var(--nb-ink)',
        borderRadius: 'var(--nb-radius)',
      }}
    />
    {onRemove && (
      <button
        type="button"
        onClick={onRemove}
        aria-label="Quitar imagen"
        className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center"
        style={{
          background: 'var(--nb-urgent)',
          border: '2px solid var(--nb-ink)',
          borderRadius: '50%',
          boxShadow: '1px 1px 0 var(--nb-ink)',
        }}
      >
        <XCircleIcon size={14} />
      </button>
    )}
  </div>
);

/** Existing image from server (has id so it can be removed) */
export interface ExistingImageItem {
  id: number;
  url: string;
}

interface ImageUploaderProps {
  /** @deprecated Prefer existingImages when you have gallery_items (ids) for remove support */
  existingImageUrls?: string[];
  /** Existing images with id (enables remove button and calls onRemoveExisting) */
  existingImages?: ExistingImageItem[];
  onImagesSelected: (images: File[]) => void;
  /** Called when user removes an existing image; only used when existingImages is passed */
  onRemoveExisting?: (id: number) => void | Promise<void>;
  /** Max images (default 4). Use 1 to replace on each crop instead of appending. */
  maxImages?: number;
  className?: string;
}

type DisplayItem =
  | { type: 'existing'; src: string; id?: number }
  | { type: 'file'; src: string; fileIndex: number };

export const ImageUploader: FC<ImageUploaderProps> = ({
  existingImageUrls = [],
  existingImages,
  onImagesSelected,
  onRemoveExisting,
  maxImages = 4,
  className,
}) => {
  const fileInputId = useId();
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [filePreviewUrls, setFilePreviewUrls] = useState<string[]>([]);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Record<
    'width' | 'height' | 'x' | 'y',
    number
  > | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [originalFile, setOriginalFile] = useState<File | null>(null);

  const existingList = existingImages ?? (existingImageUrls ?? []).map((url) => ({ id: 0, url }));

  useEffect(() => {
    const urls = selectedImages.map((file) => URL.createObjectURL(file));
    setFilePreviewUrls(urls);
    return () => {
      urls.forEach(URL.revokeObjectURL);
    };
  }, [selectedImages]);

  const displayItems = useMemo<DisplayItem[]>(() => {
    const existing: DisplayItem[] = existingList.map((item) => ({
      type: 'existing',
      src: item.url,
      id: item.id || undefined,
    }));
    const files: DisplayItem[] = filePreviewUrls.map((src, fileIndex) => ({
      type: 'file',
      src,
      fileIndex,
    }));
    return [...existing, ...files];
  }, [existingList, filePreviewUrls]);

  const totalCount = existingList.length + selectedImages.length;
  const canAddMore = totalCount < maxImages;

  const canRemoveExisting = Boolean(existingImages?.length && onRemoveExisting);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setCropImageSrc(reader.result as string);
      setOriginalFile(file);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const onCropComplete = useCallback(
    (
      _: Record<'width' | 'height' | 'x' | 'y', number>,
      croppedAreaPixels: Record<'width' | 'height' | 'x' | 'y', number>,
    ) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  const handleCrop = async () => {
    if (!cropImageSrc || !croppedAreaPixels || !originalFile) return;
    const croppedBlob = await getCroppedImg(cropImageSrc, croppedAreaPixels, rotation);
    const croppedFile = new File([croppedBlob], originalFile.name, {
      type: 'image/jpeg',
    });
    const updatedImages =
      maxImages === 1 ? [croppedFile] : [...selectedImages, croppedFile];
    setSelectedImages(updatedImages);
    onImagesSelected(updatedImages);
    setShowCropper(false);
  };

  const handleRemoveImage = (index: number) => {
    const updatedImages = selectedImages.filter((_, i) => i !== index);
    setSelectedImages(updatedImages);
    onImagesSelected(updatedImages);
  };

  return (
    <div className={className ?? 'p-4'}>
      <div className="flex flex-wrap gap-4">
        {displayItems.map((item, index) => (
          <ImageThumbnail
            key={item.type === 'existing' ? `existing-${item.id ?? index}` : `file-${item.fileIndex}`}
            src={item.src}
            alt={`Preview ${index + 1}`}
            onRemove={
              item.type === 'file'
                ? () => handleRemoveImage(item.fileIndex)
                : canRemoveExisting && item.id != null
                  ? () => onRemoveExisting?.(item.id!)
                  : undefined
            }
          />
        ))}

        {canAddMore && (
          <Label
            htmlFor={fileInputId}
            className="nb-hatch flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1"
            style={{
              border: '2px dashed var(--nb-ink)',
              background: 'var(--nb-bg)',
              borderRadius: 'var(--nb-radius)',
              color: 'var(--nb-ink)',
            }}
          >
            <PlusIcon className="h-5 w-5" />
            <span
              className="text-[9px] font-bold uppercase tracking-[0.06em]"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              Subir
            </span>
            <Input
              id={fileInputId}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
          </Label>
        )}
      </div>

      <Dialog open={showCropper} onOpenChange={setShowCropper}>
        <DialogContent
          className="flex h-[540px] w-full max-w-[92%] flex-col justify-between p-4 sm:max-w-md"
          style={{
            background: 'var(--nb-paper)',
            border: '3px solid var(--nb-ink)',
            boxShadow: '6px 6px 0 var(--nb-ink)',
            borderRadius: 'var(--nb-radius)',
          }}
        >
          <div
            className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-60"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
          >
            / recortar
          </div>
          <DialogTitle className="font-display text-[22px] leading-tight">
            Recortá la imagen
          </DialogTitle>
          <DialogDescription className="text-[12px] font-medium">
            Podés rotar y hacer zoom antes de guardar.
          </DialogDescription>
          {cropImageSrc && (
            <>
              <div
                className="relative w-full flex-1 overflow-hidden"
                style={{
                  border: '2px solid var(--nb-ink)',
                  background: '#e8e3d5',
                  borderRadius: 'var(--nb-radius)',
                }}
              >
                <Cropper
                  image={cropImageSrc}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  style={{
                    containerStyle: { width: '100%', height: '100%' },
                    cropAreaStyle: { border: '2px solid #fff', borderRadius: '4px' },
                  }}
                />
              </div>

              <div className="mt-3 flex w-full items-end gap-3">
                <div className="flex flex-1 flex-col gap-1">
                  <label
                    className="text-[10px] font-bold uppercase tracking-[0.08em]"
                    style={{ fontFamily: '"JetBrains Mono", monospace' }}
                  >
                    Zoom
                  </label>
                  <Input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="w-full border-0 px-0 shadow-none accent-black"
                  />
                </div>

                <div className="flex flex-col items-center gap-1">
                  <Label
                    className="text-[10px] font-bold uppercase tracking-[0.08em]"
                    style={{ fontFamily: '"JetBrains Mono", monospace' }}
                  >
                    Rotar
                  </Label>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setRotation((prev) => (prev - 90) % 360)}
                      className="nb-btn nb-btn-sm fill-paper"
                      style={{ padding: '4px 6px' }}
                      aria-label="Rotar 90° antihorario"
                    >
                      <RotateCcwIcon size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setRotation((prev) => (prev + 90) % 360)}
                      className="nb-btn nb-btn-sm fill-paper"
                      style={{ padding: '4px 6px' }}
                      aria-label="Rotar 90° horario"
                    >
                      <RotateCwIcon size={14} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex w-full justify-end">
                <NbButton fill="ink" onClick={handleCrop} className="px-5 py-2.5">
                  Recortar
                </NbButton>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
